<template>
	<b-modal id="add-inventory-session" title="Add Inventory Session" size="lg" ref="modal" ok-title="Add" @ok="handleOk"
		@show="onReset" :cancel-disabled="disableConfirmButtons" :ok-disabled="disableConfirmButtons"
		:no-close-on-backdrop="true">
		<loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

		<b-form @submit.stop.prevent="handleSubmit" novalidate>
			<b-container fluid>
				<b-row class="my-2">
					<b-col sm="8">
						<b>SESSION INFORMATION</b>
					</b-col>
				</b-row>
				<b-row class="my-12">
					<b-col lg="6" md="12" sm="12">
						<b-form-group label="Company">
							<v-select name="Company" class="style-chooser" label="text" placeholder=" - Please select - " :options="companyOptions"
								:reduce="(company) => company.value" v-model="selCompany" v-validate="'selectRequired'">
								<template v-slot:no-options="{ search, searching }">
									<template v-if="searching">
										No results found for
										<em>
											<strong>{{ search }}</strong>
										</em>
									</template>
									<em :style="{ opacity: 0.5 }" v-else>
										Start typing to search for a company
									</em>
								</template>
							</v-select>
							<span v-show="errors.has('Company')" class="help-block">
								{{ errors.first('Company') }}
							</span>
						</b-form-group>
					</b-col>

					<b-col lg="6" md="12" sm="12">
						<b-form-group label="Storage Location">
							<v-select name="Storage Location" class="style-chooser" label="text" placeholder=" - Please select - "
								:options="storageLocationOptions" :reduce="(loc) => loc.value" v-model="selStorageLocation"
								v-validate="'selectRequired'">
								<template v-slot:no-options="{ search, searching }">
									<template v-if="searching">
										No results found for
										<em>
											<strong>{{ search }}</strong>
										</em>
									</template>
									<em :style="{ opacity: 0.5 }" v-else>
										Start typing to search for a storage location
									</em>
								</template>
							</v-select>
							<span v-show="errors.has('Storage Location')" class="help-block">
								{{ errors.first('Storage Location') }}
							</span>
						</b-form-group>
					</b-col>
				</b-row>
				<b-row class="my-12">
					<b-col lg="6" md="6" sm="12">
						<b-form-group label="Description:" label-for="description" description>
							<b-form-textarea name="Description" type="text" v-model="form.description" maxlength="200"
								v-validate="{
									required: true,
									regex: remarksRegex
								}" :rows="3" placeholder="Description" />
							<span v-show="errors.has('Description')" class="help-block">
								{{ errors.first('Description') }}
							</span>
						</b-form-group>
					</b-col>
				</b-row>
				<b-row class="my-2">
					<b-col sm="8">
						<b>LIST OF SCANNERS</b>
					</b-col>
				</b-row>
				<b-row no-gutters class="my-12">
					<b-col sm="6" class="mr-4">
						<b-form-group label="Select Scanners" label-for="scanners"
							description="The person you want to include to scan in this inventory session">
							<v-select name="Scanner" class="style-chooser" label="text" placeholder=" - Please select - " :options="userOptions"
								:reduce="(user) => user.value" v-model="selScanner">
								<template v-slot:no-options="{ search, searching }">
									<template v-if="searching">
										No results found for
										<em>
											<strong>{{ search }}</strong>
										</em>
									</template>
									<em :style="{ opacity: 0.5 }" v-else>
										Start typing to search for a user
									</em>
								</template>
							</v-select>
						</b-form-group>
					</b-col>
					<b-col lg="3" md="4" sm="4">
						<b-button variant="primary" class="add-button" @click="addScanner">
							Add Scanner
						</b-button>
					</b-col>
				</b-row>

				<div>
					<b-row class="mt-4 mb-2">
						<b-col sm="4" class="md-left-text-sm-right">
							<b>SELECTED SCANNERS</b>
						</b-col>
					</b-row>

					<b-row class="mb-2" v-show="form.scanners.length > 0">
						<b-col sm="3" class="text-sm-left">
							<b>Email</b>
						</b-col>
						<b-col sm="3" class="text-sm-left">
							<b>Name</b>
						</b-col>
						<b-col sm="1" class="text-sm-left">
							<b>Action</b>
						</b-col>
					</b-row>
					<b-row class="mb-2" v-bind:key="scanner.id" v-for="scanner in form.scanners">
						<b-col sm="3" class="text-sm-left">
							{{ scanner.id }}
						</b-col>
						<b-col sm="3" class="text-sm-left">
							{{ scanner.name }}
						</b-col>
						<b-col sm="1" class="text-sm-left">
							<b-button size="sm" v-b-tooltip.hover.top="'Remove Scanner'" variant="danger"
								@click.stop="removeScanner(scanner.id)" class="mr-1">
								<i class="fa fa-trash"></i>
							</b-button>
						</b-col>
					</b-row>

					<b-row class="mb-2" v-show="form.scanners.length === 0">
						<b-col sm="6" class="text-sm-left">
							<i>There is no selected scanners yet.</i>
						</b-col>
					</b-row>
				</div>
			</b-container>
		</b-form>
	</b-modal>
</template>

<script>
// Util
import { DateUtil } from '@/utils/dateutil';
import { DropDownItemsUtil } from '@/utils/dropDownItemsUtil';
import { InventorySessionUtil } from '@/utils/inventorySessionUtil';
import { ValidationUtil } from '@/utils/validationUtil';

// DAO & API
import sessionDAO from '@/database/inventorySessions';
import sessionAPI from '@/api/inventorySessionApi';

// Others
import config from '@/config/env-constants';
import EventBus from '@/shared/event-bus';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import _ from 'lodash';


export default {
	name: 'add-inventory-session',
	components: {
		Loading,
	},
	props: {
		allCompanyOptions: {
			type: Array,
			required: true,
		},
		allStorageLocationOptions: {
			type: Array,
			required: true,
		},
		allUserOptions: {
			type: Array,
			required: true,
		},
		isSuperAdmin: {
			type: Boolean,
			required: true,
		},
	},
	data() {
		return {
			form: { ...InventorySessionUtil.getDefaultSessionObj() },

			companyOptions: [],
			storageLocationOptions: [],
			userOptions: [],

			selCompany: config.companyDefaultValue,
			selStorageLocation: config.storageLocationDefaultValue,
			selScanner: config.userDefaultValue,
			selScanners: {},

			loggedUser: this.$store.getters.loggedUser,
			loggedUserCompany: this.$store.getters.loggedUserCompany,

			// Check for loader
			isLoading: false,
		};
	},
	watch: {
		allCompanyOptions: function () {
			this.companyOptions = this.allCompanyOptions;
			this.storageLocationOptions.push({
				value: config.storageLocationDefaultValue,
				text: ' - Please select - ',
			});
			this.userOptions.push({
				value: config.userDefaultValue,
				text: ' - Please select - ',
			});
		},
		selCompany: function () {
			// reset selected scanners
			this.form.scanners = [];

			if (this.selCompany.id) {
				this.retrieveStorageLocation();
				this.retrieveUsers();
			}
		},
	},
	computed: {
		disableConfirmButtons() {
			return this.isLoading;
		},
		remarksRegex() {
			return config.remarksRegex;
		}
	},
	methods: {
		retrieveStorageLocation() {
			this.storageLocationOptions = [];
			this.storageLocationOptions.push({
				value: config.storageLocationDefaultValue,
				text: ' - Please select - ',
			});
			this.allStorageLocationOptions.forEach((loc) => {
				if (loc.value.companyId === this.selCompany.id) {
					this.storageLocationOptions.push(loc);
				}
			});

			this.selStorageLocation = config.storageLocationDefaultValue;
		},
		retrieveUsers() {
			this.userOptions = [];

			this.userOptions.push({
				value: config.userDefaultValue,
				text: ' - Please select - ',
			});

			this.allUserOptions.forEach((user) => {
				if (user.value.companyId === this.selCompany.id) {
					this.userOptions.push(user);
				} else if (user.value.type === config.managerRole && user.value.id === this.loggedUser.id) {
					this.userOptions.push(user);
				}
			});

			this.selScanner = config.userDefaultValue;
		},
		isExistingScanner(userId) {
			return ValidationUtil.arrayHasValueDirectField(
				this.form.scanners,
				'id',
				userId
			);
		},
		addScanner() {
			if (this.selScanner.id === null) {
				this.$toaster.warning('Please select a scanner to add.');
				return;
			}

			if (this.isExistingScanner(this.selScanner.id)) {
				let scannerName = this.selScanner.name;
				this.$toaster.warning(`Scanner "${scannerName}" already added.`);
				return;
			}

			let scanner = { ...this.selScanner };
			scanner['scannedAssets'] = [];
			scanner['isDone'] = 'false';
			this.form.scanners.push(scanner);
		},
		removeScanner(scannerId) {
			let index = _.findIndex(this.form.scanners, o => {
				return o.id === scannerId;
			});

			if (index < 0) {
				this.$toaster.warning(`Scanner "${scannerId}" does not exists.`);
				return;
			}

			let scanner = this.form.scanners[index];
			if (scanner.scannedAssets.length > 0) {
				this.$toaster.warning(`Removing Scanner "${scannerId}" is not allowed since he/she already have scanned assets. `);
				return;
			}

			// delete scanner
			this.form.scanners.splice(index, 1);
			delete this.selScanners[scannerId];
		},

		async getOngoingSessionIds() {
			let companyId = this.selCompany.id;
			let allowedScannerIds = _.map(this.form.scanners, 'id');

			for (let userId of allowedScannerIds) {
				let sessionsObj = await sessionDAO.getInventorySessions(companyId, userId);
				let onGoingSessionsObj = _.filter(sessionsObj, o => {
					return o.status === 'Draft' || o.status === 'On-Going';
				});

				if (!_.isEmpty(onGoingSessionsObj)) {
					return _.join(_.map(onGoingSessionsObj, 'sessionId'), ',');
				}
			}

			return "";
		},
		async handleOk(evt) {
			// Prevent modal from closing
			evt.preventDefault();

			let isValid = await this.$validator.validateAll();
			if (!isValid) {
				this.$toaster.warning('Please address the field/s with invalid input.');
				return;
			} else if (this.form.scanners.length === 0) {
				this.$toaster.warning('At least 1 selected scanner is required.');
				return;
			}

			let ongoingSessionIds = await this.getOngoingSessionIds();
			if (ongoingSessionIds.length > 0) {
				let companyName = this.selCompany.name;
				let scanners = _.join(_.map(this.form.scanners, 'id'), ',');
				this.$toaster.warning('You have On-Going session(s), ' + ongoingSessionIds + ', under company ' + companyName + ' for these scanner(s), ' + scanners + '.');
				return;
			}

			await this.handleSubmit();
		},
		async handleSubmit() {
			// show loading indicator
			this.isLoading = true;

			// Update form fields
			this.form.sessionId = 'IS' + DateUtil.getCurrentTimestamp();
			this.updateCompanyAndLocation();
			this.updateAllowedScanners();
			this.form.dateUpdated = DateUtil.getCurrentTimestamp();
			this.form.updatedBy = this.loggedUser.id;

			let sessionId = this.form.sessionId;
			try {
				let { data } = await sessionAPI.addInventorySession(
					this.form,
					this.loggedUser.id,
					DateUtil.getCurrentTimestamp()
				);

				if (data.isSuccess) {
					this.$toaster.success(data.message);
					EventBus.$emit('onCloseSaveSession', data.inventorySession);
					this.$refs.modal.hide();
				} else {
					this.$toaster.error(`Error creating Inventory Session "${sessionId}". Please try again.`);
				}
			} catch (_error) {
				this.$toaster.error(`Error creating Inventory Session "${sessionId}". Please try again.`);
			} finally {
				// hide loading indicator
				this.isLoading = false;
			}
		},
		updateCompanyAndLocation() {
			this.form.company = this.selCompany.name;
			this.form.companyId = this.selCompany.id;
			this.form.storageLocation = this.selStorageLocation.name;
			this.form.storageLocationId = this.selStorageLocation.id;
		},
		updateAllowedScanners() {
			this.form.allowedScanners = _.map(this.form.scanners, 'id');
		},

		onReset() {
			/* Reset our form values */
			this.form = { ...InventorySessionUtil.getDefaultSessionObj() };

			if (!this.isSuperAdmin) {
				// pre-select the current company
				this.selCompany = DropDownItemsUtil.getCompanyItem(
					this.loggedUserCompany
				);
			} else {
				this.selCompany = config.companyDefaultValue;
			}

			this.selStorageLocation = config.storageLocationDefaultValue;
			this.selScanner = config.userDefaultValue;
			this.selScanners = {};

			// reset validation
			this.$validator.reset();
			this.errors.clear();
		},
	},
};
</script>